import type {
	PbLineItem,
	PbLineItemPackage,
	PbPackageProduct,
} from '@/apiTypes'
import type Plan from '@/classes/Plan'
import type { PriceBook } from '@/classes/PriceBook'
import { Disposition } from '@/enums/Disposition'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { ViewingOption } from '@/enums/ViewingOption'
import { usePlanStore } from '@/stores/plan'

export enum Reason {
	Burial = 'burial',
	Cremation = 'cremation',
	Gathering = 'gathering',
	Memorial = 'memorial',
	Viewing = 'viewing',
	Meal = 'meal',
}

export type DetailedPackageLines = Array<
	PbPackageProduct & { lineItem: PbLineItem | undefined }
>

type Evaluation = {
	score: number
	reasons: Reason[]
	isDirectPackage: boolean
}

type PackageEvaluation = {
	pkg: PbLineItemPackage
	lineItems: DetailedPackageLines
	evaluation: Evaluation
}

export type PackageRecommendation = {
	plan: Plan
	pkg: PbLineItemPackage
	reasons: Reason[]
}

/** Get an array of recommended packages, up to 1 per plan */
export function getPackageRecommendations(
	priceBook: PriceBook
): Array<PackageRecommendation> {
	const planStore = usePlanStore()

	return planStore.plans
		.map((plan) => {
			const packageEval = getPlanPackageRecommendation(plan, priceBook)

			return packageEval === undefined
				? undefined
				: {
						plan,
						pkg: packageEval.pkg,
						reasons: packageEval.evaluation.reasons,
					}
		})
		.filter((rec) => rec?.pkg) as Array<PackageRecommendation>
}

/** Get 1 recommended package for the plan, or undefined if no matches */
export function getPlanPackageRecommendation(
	plan: Plan,
	priceBook: PriceBook
): PackageEvaluation | undefined {
	// Score all packages
	const packages: PackageEvaluation[] =
		priceBook.packages
			.filter((pkg) => pkg.subcategory !== PbSubcategory.PackageEssentials)
			.map((pkg) => {
				const lineItems = priceBook.getProductsInPackage(pkg.id) ?? []

				return {
					pkg,
					lineItems,
					evaluation: getPlanPackageEval(plan, pkg, lineItems),
				}
			})
			.filter((pkg) => pkg.evaluation.score > 0) ?? []

	// Pick the single best package
	const best =
		packages.length === 0
			? undefined
			: packages.sort((a, b) => {
					const diff = b.evaluation.score - a.evaluation.score
					if (diff !== 0) return diff
					return b.pkg.fixedPrice - a.pkg.fixedPrice
				})[0]

	// Don't recommend direct packages
	if (best === undefined || best.evaluation.isDirectPackage) return undefined

	return best
}

/** Get the score for a particular package for a plan */
export function getPlanPackageEval(
	plan: Plan,
	pkg: PbLineItemPackage,
	packageLineItems: DetailedPackageLines
): Evaluation {
	let score = 0
	const reasons: Reason[] = []
	let packageServiceCount = 0

	const zero = {
		score: 0,
		reasons: [],
		isDirectPackage: false,
	}

	/** Helper for adjusting score and reasons based on a plan's want and package contents */
	function evaluateFeature(
		wantsFeature: boolean,
		subcategory: PbSubcategory,
		reason: Reason,
		{ positivePoints, negativePoints } = {
			positivePoints: 1,
			negativePoints: 0.1,
		}
	) {
		const hasFeature = packageContainsSubcategory(packageLineItems, subcategory)
		if (hasFeature) packageServiceCount++

		if (wantsFeature && hasFeature) {
			score += positivePoints
			reasons.push(reason)
		}

		if (!wantsFeature && !hasFeature) {
			score += negativePoints
		}
	}

	// Return zero if disposition does not match
	if (
		pkg.subcategory === PbSubcategory.PackageBurial &&
		plan.burialOrCremation === Disposition.Burial
	) {
		reasons.push(Reason.Burial)
	} else if (
		pkg.subcategory === PbSubcategory.Cremation &&
		plan.burialOrCremation === Disposition.Cremation
	) {
		reasons.push(Reason.Cremation)
	} else {
		return zero
	}

	// Connecting: gatheringLocation and Graveside
	const wantsGraveside = !!plan.placeOfGathering
	evaluateFeature(
		wantsGraveside,
		PbSubcategory.ServiceGravesideService,
		Reason.Gathering
	)

	// See: viewingPreference and Visitation/Viewing
	const wantsViewing = [ViewingOption.Public, ViewingOption.Private].includes(
		plan.viewingPreference
	)
	evaluateFeature(
		wantsViewing,
		PbSubcategory.ServiceVisitationViewing,
		Reason.Viewing
	)

	// Honor: placeOfService and Funeral/Memorial
	const wantsFuneral = plan.placeOfService != ''
	evaluateFeature(
		wantsFuneral,
		PbSubcategory.ServiceFuneralMemorial,
		Reason.Memorial
	)

	// Celebrate: postServiceMeal and catering
	// Bonus point only: no point if missing meal
	const wantsMeal = plan.postServiceMeal
	evaluateFeature(wantsMeal, PbSubcategory.AfterwardCatering, Reason.Meal, {
		positivePoints: 0.5,
		negativePoints: 0,
	})

	// Return zero if the user has entered no preferences
	// This is to avoid showing direct cremation if the customer
	// just skipped all the questions
	if (!wantsGraveside && !wantsViewing && !wantsFuneral && !wantsMeal) {
		return zero
	}

	return { score, reasons, isDirectPackage: packageServiceCount === 0 }
}

export function packageContainsSubcategory(
	lines: DetailedPackageLines,
	subcategory: PbSubcategory
) {
	return lines.some(
		(line) =>
			line.lineItem?.subcategory === subcategory &&
			!line.lineItem?.isVirtualLine
	)
}
